import React, { useState, useEffect } from 'react';
import { X, User, Mail, Key, Eye, EyeOff, Copy, Check, Crown } from 'lucide-react';
import { superAdminAPI } from '../../services/api';

const SuperAdminModal = ({ isOpen, mode, superAdmin, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    enabled: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isAddMode = mode === 'add';

  useEffect(() => {
    if (isOpen) {
      if (superAdmin && (isEditMode || isViewMode)) {
        setFormData({
          name: superAdmin.name || '',
          email: superAdmin.email || '',
          enabled: superAdmin.enabled !== undefined ? superAdmin.enabled : true
        });
      } else if (isAddMode) {
        setFormData({
          name: '',
          email: '',
          enabled: true
        });
      }
      setErrors({});
      setGeneratedPassword('');
      setShowPassword(false);
      setPasswordCopied(false);
    }
  }, [isOpen, superAdmin, mode]);

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: newValue }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isAddMode) {
        // Generate password for new super admin
        const password = generatePassword();
        setGeneratedPassword(password);

        const superAdminData = {
          ...formData,
          password: password,
          roles: ['ROLE_SUPER_ADMIN'],
          isFirstTimeLogin: true,
          emailVerified: false
        };

        await superAdminAPI.createSuperAdmin(superAdminData);

        // Call onSave to refresh the list, but don't close the modal
        onSave();

        // Reset form for potential next super admin creation
        setFormData({
          name: '',
          email: '',
          enabled: true
        });

      } else if (isEditMode) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          enabled: formData.enabled
        };

        await superAdminAPI.updateUser(superAdmin.id, updateData);

        // For edit mode, call onSave and close the modal
        onSave();
        onClose();
      }

    } catch (error) {
      console.error('Error saving super admin:', error);
      alert('Failed to save super admin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Crown className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isAddMode && 'Add Super Admin'}
                {isEditMode && 'Edit Super Admin'}
                {isViewMode && 'Super Admin Details'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isAddMode && 'Create a new super administrator with system-wide access'}
                {isEditMode && 'Update super administrator information'}
                {isViewMode && 'View super administrator details and manage account'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Generated Password Display */}
          {generatedPassword && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">
                Generated Password
              </h3>
              <div className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={generatedPassword}
                    readOnly
                    className="input-field pr-20 bg-white"
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={copyPassword}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {passwordCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-yellow-700 mt-2">
                Please save this password securely. The super admin will need to change it on first login.
              </p>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isViewMode}
                className={`input-field ${errors.name ? 'border-red-300' : ''}`}
                placeholder="e.g., John Doe"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  className={`input-field pl-10 ${errors.email ? 'border-red-300' : ''}`}
                  placeholder="e.g., admin@example.com"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Status */}
          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="enabled"
                checked={formData.enabled}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Account is active</span>
            </label>
          </div>

          {/* Super Admin Info */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center mb-2">
              <Crown className="h-5 w-5 text-yellow-600 mr-2" />
              <h3 className="text-sm font-medium text-blue-800">Super Administrator Privileges</h3>
            </div>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Full system access across all tenants</li>
              <li>• Tenant management and configuration</li>
              <li>• User management and administration</li>
              <li>• System monitoring and health checks</li>
              <li>• Question and content management</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex justify-end pt-6 border-t mt-6">
            <div className="flex space-x-3">
              {/* Show different buttons based on whether super admin was created successfully */}
              {isAddMode && generatedPassword ? (
                // Super admin was created successfully - show Create Another and Close buttons
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setGeneratedPassword('');
                      setShowPassword(false);
                      setPasswordCopied(false);
                    }}
                    className="btn-secondary"
                  >
                    Create Another
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-primary"
                  >
                    Close
                  </button>
                </>
              ) : (
                // Normal state - show Cancel/Close and Create/Update buttons
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                  >
                    {isViewMode ? 'Close' : 'Cancel'}
                  </button>
                  {!isViewMode && (
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : isAddMode ? 'Create Super Admin' : 'Update Super Admin'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuperAdminModal;
